<template>
    <HomeSectionContainer title="Communicating with Nadia">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-stretch">
            <AccountSetting :error="errors[FIELDS.LANGUAGE]">
                <template #label>Language</template>
                <template #content>
                    <AccountSettingSelect
                        :id="idForField(FIELDS.LANGUAGE)"
                        aria-label="Language"
                        :choices="languageChoices"
                        :model-value="preferenceValues[FIELDS.LANGUAGE] || ''"
                        @update:model-value="(value) => setPreference(FIELDS.LANGUAGE, value)" />
                </template>
            </AccountSetting>
            <AccountSetting :error="errors.choice">
                <template #label>Cookies</template>
                <template #content>
                    <AccountSettingSelect
                        :id="idForField(FIELDS.COOKIE_CONSENT_CHOICE)"
                        aria-label="Cookies"
                        :choices="cookieConsentChoices"
                        :model-value="preferenceValues[FIELDS.COOKIE_CONSENT_CHOICE] || ''"
                        @update:model-value="(value) => setPreference(FIELDS.COOKIE_CONSENT_CHOICE, value)" />
                </template>
            </AccountSetting>
            <AccountSetting v-if="canReceiveEmails" :error="errors[FIELDS.CHECK_INS]">
                <template #label>Check-ins</template>
                <template #content>
                    <AccountSettingToggle
                        :id="idForField(FIELDS.CHECK_INS)"
                        label="Weekly check-ins"
                        :model-value="preferenceValues[FIELDS.CHECK_INS]"
                        @update:model-value="(value) => setPreference(FIELDS.CHECK_INS, value)"></AccountSettingToggle>
                </template>
            </AccountSetting>
            <AccountSetting :error="errors[FIELDS.VOICE_ENABLED]">
                <template #label>Voice</template>
                <template #content>
                    <AccountSettingToggle
                        :id="idForField(FIELDS.VOICE_ENABLED)"
                        label="Default to voice?"
                        :model-value="preferenceValues[FIELDS.VOICE_ENABLED]"
                        @update:model-value="(value) => setPreference(FIELDS.VOICE_ENABLED, value)"></AccountSettingToggle>
                </template>
            </AccountSetting>
            <AccountSetting v-if="micConsent.policy_enabled">
                <template #label>Microphone</template>
                <template #content>
                    <AccountSettingToggle
                        id="mic-consent-toggle"
                        label="Allow voice input"
                        :model-value="micConsentGranted"
                        @update:model-value="setMicConsent"></AccountSettingToggle>
                    <p v-if="micConsentError" class="body-small text-red-600" data-testid="mic-consent-error">Couldn't save that just now. Try again.</p>
                </template>
            </AccountSetting>
            <AccountSetting :error="errors[FIELDS.IS_MANAGER]">
                <template #label>Manager</template>
                <template #content>
                    <AccountSettingToggle
                        :id="idForField(FIELDS.IS_MANAGER)"
                        label="I lead a team"
                        :model-value="preferenceValues[FIELDS.IS_MANAGER]"
                        @update:model-value="(value) => setPreference(FIELDS.IS_MANAGER, value)"></AccountSettingToggle>
                </template>
            </AccountSetting>
        </div>
        <AccountSetting v-if="customInstructionsEnabled" class="mt-3" :error="errors[FIELDS.CUSTOM_INSTRUCTIONS]">
            <template #label>Custom Instructions</template>
            <template #content>
                <AccountSettingTextarea
                    :id="idForField(FIELDS.CUSTOM_INSTRUCTIONS)"
                    aria-label="Custom Instructions"
                    :maxlength="1500"
                    placeholder="Tell Nadia anything you'd like her to keep in mind during your conversations..."
                    :model-value="preferenceValues[FIELDS.CUSTOM_INSTRUCTIONS]"
                    @save="(value) => setPreference(FIELDS.CUSTOM_INSTRUCTIONS, value)" />
            </template>
        </AccountSetting>
    </HomeSectionContainer>
</template>

<script>
export const ERROR_BAG = "account_settings";
</script>

<script setup>
import { getCookie } from "/js/utils.js";
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import HomeSectionContainer from "~vue/ChatHome/HomeSectionContainer.vue";
import AccountSetting from "~vue/UserProfile/AccountSetting.vue";
import AccountSettingSelect from "~vue/UserProfile/AccountSettingSelect.vue";
import AccountSettingTextarea from "~vue/UserProfile/AccountSettingTextarea.vue";
import AccountSettingToggle from "~vue/UserProfile/AccountSettingToggle.vue";
import { ref, toRef, watch } from "vue";

const FIELDS = {
    LANGUAGE: "default_language",
    CHECK_INS: "prefers_weekly_check_in",
    VOICE_ENABLED: "prefers_default_to_voice",
    IS_MANAGER: "is_manager",
    COOKIE_CONSENT_CHOICE: "cookie_consent_choice",
    CUSTOM_INSTRUCTIONS: "custom_instructions",
};

const props = defineProps({
    userProfile: { type: Object, required: true },
    languageChoices: { type: Array, required: true },
    cookieConsentChoices: { type: Array, required: true },
    errors: { type: Object, default: () => ({}) },
    endpoints: { type: Object, required: true },
    canReceiveEmails: { type: Boolean, default: true },
    customInstructionsEnabled: { type: Boolean, default: false },
    micConsent: { type: Object, default: () => ({}) },
});

/*
 * toRef is used to support optimistic updates. State is mutated
 * on the client, and then hydrated with any updates to the page props
 * as caused by the `router` requests below.
 */
const preferenceValues = toRef(() => props.userProfile);

// Consent is a UserUIInteractions row rather than a profile field, so it is written through its own
// endpoint rather than the profile POST the other settings use.
const micConsentGranted = ref(props.micConsent.granted);
const micConsentError = ref(false);
const { execute: grantMicConsent, error: grantError } = useFetch(props.endpoints.mic_consent, { immediate: false }).post().json();
const { execute: revokeMicConsent, error: revokeError } = useFetch(props.endpoints.mic_consent, { immediate: false }).delete().json();

// A server round trip can change it too (revoking elsewhere), so track the prop rather than only
// seeding from it once.
watch(
    () => props.micConsent.granted,
    (granted) => (micConsentGranted.value = granted),
);

async function setMicConsent(granted) {
    const previous = micConsentGranted.value;
    micConsentGranted.value = granted;
    micConsentError.value = false;

    const [write, error] = granted ? [grantMicConsent, grantError] : [revokeMicConsent, revokeError];
    await write();

    // useFetch runs with throwOnFailed = false, so a failed write resolves like a success. Roll the
    // toggle back rather than showing consent the server never recorded.
    if (error.value) {
        micConsentGranted.value = previous;
        micConsentError.value = true;
        return;
    }

    // mic_consent_required is resolved server-side and read from shared props by every mic surface,
    // so refresh it here or a revoke leaves the mic unlocked until the next full page load.
    router.reload({ only: ["user_details", "mic_consent"] });
}

// Cookie preference update via API (not Inertia)
const cookiePreferencePayload = ref(null);
const { execute: updateCookiePreference } = useFetch(props.endpoints.cookie_preference, { immediate: false }).put(cookiePreferencePayload).json();

async function setCookiePreference(value) {
    preferenceValues.value[FIELDS.COOKIE_CONSENT_CHOICE] = value;
    cookiePreferencePayload.value = { choice: value, method: "profile" };
    await updateCookiePreference();
    window.location.reload();
}

function setPreference(field, value) {
    // Cookie preferences use direct API call instead of Inertia
    if (field === FIELDS.COOKIE_CONSENT_CHOICE) {
        setCookiePreference(value);
        return;
    }

    const body = { [field]: value, detail: field };
    preferenceValues.value[field] = value;

    router.post(props.endpoints.profile, body, {
        errorBag: ERROR_BAG,
        headers: {
            "X-CSRFToken": getCookie("csrftoken"),
            "X-Inertia-Next": props.endpoints.current_page,
        },
        preserveScroll: true,
        only: ["user_profile", "errors", "user_details"],
    });
}

function idForField(field) {
    return `s_${field}`;
}
</script>
